import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';

import type { ApiClient } from '../api/client';
import type {
  CreateNativePtySessionRequest,
  CreateSidecarShellSessionRequest,
  Session,
  SessionTerminalWsTokenResponse
} from '../types';
import {
  TerminalFullscreenExitButton,
  TerminalScrollModeControl,
  TerminalSpecialKeyToolbar,
  useTerminalFocusRecovery,
  useTerminalMaximize,
  useTerminalScrollMode,
  useTerminalSoftKeyModifiers,
  useTerminalTouchScroll
} from './TerminalControls';
import { TerminalFontSizeControl, useTerminalFontSize } from './TerminalFontSizePreference';
import { configureTerminalTextInput } from './TerminalTextInput';
import { installTerminalAndroidImeDiff } from './TerminalAndroidIme';
import {
  copyTerminalText,
  readTerminalClipboardText,
  TerminalSelectionActions,
  useTerminalTouchSelection,
  type TerminalTouchGestureOwner
} from './TerminalSelection';
import {
  appendRequestId,
  formatWebSocketDisconnectedMessage,
  formatWebSocketTokenFailureMessage
} from '../utils/errors';
import { usePersistentTerminalConnection } from './usePersistentTerminalConnection';
import { useTerminalViewportDebug } from './TerminalViewportDebug';

const terminalOverviewRuler = () => ({
  width: window.matchMedia('(pointer: coarse)').matches ? 1 : 14
});

type SessionTerminalPanelProps = {
  apiClient: ApiClient;
  conversationId: string;
  sessions: Session[];
  canStartSessions: boolean;
  selectedSessionId: string | null;
  onSelectedSessionChange: (sessionId: string | null) => void;
  /** The attached session that may be resumed after a fail-closed reauthorization. */
  resumeSessionId: string | null;
  onAttachedSessionChange: (sessionId: string) => void;
  onClearResumeSession: () => void;
  /** True only after a post-reauthorization session listing completed. */
  sessionsFreshForAuthorization: boolean;
  canAutomaticallyReconnect: () => boolean;
  onRefreshSessions: () => void;
  setError: (message: string | null) => void;
};

function terminalWsUrl(
  conversationId: string,
  sessionId: string,
  token: string,
  requestId?: string | null,
  size?: { cols: number; rows: number } | null
) {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams({ ws_token: token });
  if (requestId) {
    params.set('client_request_id', requestId);
  }
  if (size && size.cols > 0 && size.rows > 0) {
    params.set('cols', String(size.cols));
    params.set('rows', String(size.rows));
  }
  return `${protocol}//${window.location.host}/api/conversations/${conversationId}/sessions/${sessionId}/terminal/ws?${params.toString()}`;
}

function canStreamTerminal(session: Session) {
  return session.capabilities.includes('stream_terminal');
}

function canAttachTerminal(session: Session) {
  return canStreamTerminal(session) && !['stopped', 'failed', 'expired'].includes(session.status);
}

function canAcceptPrompt(session: Session) {
  return session.capabilities.includes('accept_prompt_input');
}

function canOpenSidecarShell(session: Session) {
  return session.capabilities.includes('stream_structured_events');
}

export function SessionTerminalPanel({
  apiClient,
  conversationId,
  sessions,
  canStartSessions,
  selectedSessionId,
  onSelectedSessionChange,
  resumeSessionId,
  onAttachedSessionChange,
  onClearResumeSession,
  sessionsFreshForAuthorization,
  canAutomaticallyReconnect,
  onRefreshSessions,
  setError: setGlobalError
}: SessionTerminalPanelProps) {
  const terminalSessions = useMemo(() => sessions.filter(canStreamTerminal), [sessions]);
  const structuredSessions = useMemo(() => sessions.filter(canOpenSidecarShell), [sessions]);
  const [sidecarParentSessionId, setSidecarParentSessionId] = useState<string | null>(null);
  const [promptValue, setPromptValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [startProfile, setStartProfile] = useState<CreateNativePtySessionRequest['profile']>('generic-cli-pty');
  const [startCommand, setStartCommand] = useState('bash -l');
  const [sidecarCommand, setSidecarCommand] = useState('');
  const [startBusy, setStartBusy] = useState(false);
  const [hasTerminalSelection, setHasTerminalSelection] = useState(false);
  const [terminalContextOpen, setTerminalContextOpen] = useState(false);
  const [terminalSelectionMode, setTerminalSelectionMode] = useState(false);
  const terminalHostRef = useRef<HTMLDivElement | null>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const terminalTouchGestureRef = useRef<TerminalTouchGestureOwner>('idle');
  const fitAddonRef = useRef<FitAddon | null>(null);
  const lastSizeRef = useRef<{ cols: number; rows: number } | null>(null);
  // Terminal output and the socket stay component-local so an authorization
  // refresh tears both down before the new access summary is known.
  const attachingSessionIdRef = useRef<string | null>(null);
  const autoAttachAttemptedSessionIdRef = useRef<string | null>(null);
  const {
    panelRef,
    isMaximized,
    toggleMaximize,
    terminalViewportStyle,
    terminalKeyboardState,
    terminalKeyboardOverdrawGuard,
    terminalViewportFrameLayout
  } = useTerminalMaximize<HTMLDivElement>();
  const {
    enabled: terminalViewportDebugEnabled,
    pasteReport: pasteTerminalViewportDebugReport
  } = useTerminalViewportDebug({ isMaximized, panelRef, terminalRef });
  const { modifiers, toggleModifier, consumeShortcut, transformInput } = useTerminalSoftKeyModifiers();
  const { scrollMode, setScrollMode } = useTerminalScrollMode();
  const { fontSize } = useTerminalFontSize();

  const selectedSession =
    terminalSessions.find((session) => session.id === selectedSessionId) ??
    terminalSessions.find(canAttachTerminal) ??
    terminalSessions[0] ??
    null;
  const selectedId = selectedSession?.id ?? null;
  const selectedSidecarParent =
    structuredSessions.find((session) => session.id === sidecarParentSessionId) ?? structuredSessions[0] ?? null;
  const selectedSessionAttachable = selectedSession ? canAttachTerminal(selectedSession) : false;

  const {
    connectionState,
    connect: attach,
    disconnect: disconnectAttachment,
    stop: stopAttachment,
    send,
    fitAndSendResize: refitAndSendResize
  } = usePersistentTerminalConnection({
    requestToken: (sessionId) =>
      apiClient.post<SessionTerminalWsTokenResponse>(
        `/api/conversations/${conversationId}/sessions/${sessionId}/terminal/ws-token`
      ),
    socketUrl: (sessionId, token, requestId) =>
      terminalWsUrl(conversationId, sessionId, token, requestId, lastSizeRef.current),
    fitAndSendResize,
    onText: (text) => terminalRef.current?.write(text),
    onBinary: (bytes) => terminalRef.current?.write(new Uint8Array(bytes)),
    onAttached: () => {
      // Reconnects are asynchronous and must not make unsolicited IME requests.
      // A terminal tap or soft key restores focus from a trusted gesture.
      const attachedSessionId = attachingSessionIdRef.current;
      if (attachedSessionId) {
        onAttachedSessionChange(attachedSessionId);
      }
      onRefreshSessions();
    },
    onDetached: () => {
      terminalRef.current?.clearSelection();
      clearResumeSession();
      onRefreshSessions();
    },
    onProtocolError: (message, requestId) => {
      terminalRef.current?.clearSelection();
      clearResumeSession();
      const formatted = appendRequestId(message, requestId);
      setError(formatted);
      setGlobalError(formatted);
    },
    // ConversationPage owns foreground reauthorization. Its synchronous gate
    // prevents the hook from requesting a new token before React has removed
    // this sensitive UI for the access check.
    canAutomaticallyReconnect,
    onReconnectExhausted: ({ event, requestId, tokenError }) => {
      terminalRef.current?.clearSelection();
      clearResumeSession();
      const message =
        tokenError === undefined
          ? formatWebSocketDisconnectedMessage({
              context: 'Terminal disconnected',
              detail: 'The terminal could not reconnect automatically. Attach again to resume the executor PTY.',
              event,
              requestId
            })
          : formatWebSocketTokenFailureMessage('Terminal could not reconnect', tokenError);
      setError(message);
      setGlobalError(message);
    }
  });
  const connect = useCallback(async (sessionId = selectedId, candidate?: Session) => {
    if (!sessionId) return;
    const session = candidate ?? terminalSessions.find((item) => item.id === sessionId);
    if (!session || !canAttachTerminal(session)) return;
    attachingSessionIdRef.current = sessionId;
    // Avoid treating a just-user-attached session as a remount resume. A new
    // panel starts with an empty ref and may then auto-attach the persisted ID.
    autoAttachAttemptedSessionIdRef.current = sessionId;
    setError(null);
    await attach(sessionId);
  }, [attach, selectedId, terminalSessions]);

  const showTerminalWorkspace = Boolean(selectedSession || connectionState !== 'idle' || error || startBusy);
  useTerminalTouchSelection(
    terminalHostRef,
    terminalRef,
    {
      enabled: true,
      gestureOwnerRef: terminalTouchGestureRef,
      immediate: isMaximized && terminalSelectionMode,
      onContextMenuChange: setTerminalContextOpen,
      onImmediateSelectionComplete: setTerminalSelectionMode
    }
  );
  useTerminalTouchScroll(
    terminalHostRef,
    terminalRef,
    true,
    undefined,
    scrollMode,
    terminalTouchGestureRef
  );
  const focusTerminal = useTerminalFocusRecovery(
    terminalHostRef,
    terminalRef,
    showTerminalWorkspace
  );

  useEffect(() => {
    if (!resumeSessionId || !sessionsFreshForAuthorization) return;
    const resumeSession = terminalSessions.find((session) => session.id === resumeSessionId);
    // The session list was fetched after the new access summary granted
    // terminal:attach. If that exact session disappeared or no longer accepts
    // a terminal, do not retain an intent that could be reused on a later mount.
    if (!resumeSession || !canAttachTerminal(resumeSession)) {
      attachingSessionIdRef.current = null;
      onClearResumeSession();
    }
  }, [onClearResumeSession, resumeSessionId, sessionsFreshForAuthorization, terminalSessions]);

  useEffect(() => {
    if (
      !resumeSessionId ||
      !sessionsFreshForAuthorization ||
      !canAutomaticallyReconnect() ||
      autoAttachAttemptedSessionIdRef.current === resumeSessionId
    ) return;
    const resumeSession = terminalSessions.find((session) => session.id === resumeSessionId);
    if (!resumeSession) return;
    if (!canAttachTerminal(resumeSession)) return;
    autoAttachAttemptedSessionIdRef.current = resumeSessionId;
    onSelectedSessionChange(resumeSessionId);
    // The parent only supplies this ID after a prior attached acknowledgement.
    // This call therefore happens only after foreground reauthorization has
    // remounted the panel under a fresh terminal:attach grant.
    void connect(resumeSessionId, resumeSession);
  }, [
    canAutomaticallyReconnect,
    connect,
    onSelectedSessionChange,
    resumeSessionId,
    sessionsFreshForAuthorization,
    terminalSessions
  ]);

  useEffect(() => {
    const host = terminalHostRef.current;
    if (!host) {
      return;
    }
    const terminal = new Terminal({
      convertEol: false,
      cursorBlink: true,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize,
      overviewRuler: terminalOverviewRuler(),
      scrollback: 5000,
      theme: {
        background: '#0b1220',
        foreground: '#e5edf7',
        cursor: '#8bd5ff'
      }
    });
    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.open(host);
    configureTerminalTextInput(terminal.textarea);
    // GBoard rewrites break xterm's naive textarea diffing; see module docs.
    const disposeAndroidImeDiff = installTerminalAndroidImeDiff(terminal);
    terminalRef.current = terminal;
    fitAddonRef.current = fitAddon;

    const sendResize = (cols: number, rows: number) => {
      if (cols < 1 || rows < 1) {
        return;
      }
      lastSizeRef.current = { cols, rows };
      send({ type: 'resize', cols, rows });
    };
    const fit = () => {
      try {
        fitAddon.fit();
      } catch {
        // The host can be hidden while the panel is collapsed; retry on the
        // next resize/attach rather than treating this as a terminal error.
      }
      sendResize(terminal.cols, terminal.rows);
    };
    const dataDisposable = terminal.onData((data) => {
      send({ type: 'input', data: transformInput(data) });
    });
    const selectionDisposable = terminal.onSelectionChange(() => {
      setHasTerminalSelection(terminal.hasSelection());
    });
    const resizeDisposable = terminal.onResize(({ cols, rows }) => sendResize(cols, rows));
    const resizeObserver = new ResizeObserver(fit);
    resizeObserver.observe(host);
    fit();

    return () => {
      resizeObserver.disconnect();
      dataDisposable.dispose();
      selectionDisposable.dispose();
      resizeDisposable.dispose();
      disposeAndroidImeDiff?.();
      terminal.dispose();
      terminalRef.current = null;
      fitAddonRef.current = null;
    };
  // Font size is applied by the live update effect below so xterm scrollback is retained.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [send, showTerminalWorkspace, transformInput]);

  useEffect(() => {
    const terminal = terminalRef.current;
    if (!terminal) return;
    terminal.options.fontSize = fontSize;
    const frame = window.requestAnimationFrame(() => refitAndSendResize());
    return () => window.cancelAnimationFrame(frame);
  }, [fontSize, refitAndSendResize]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      refitAndSendResize();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isMaximized, refitAndSendResize]);

  function fitAndSendResize(ws: WebSocket) {
    const fitAddon = fitAddonRef.current;
    const terminal = terminalRef.current;
    if (fitAddon && terminal) {
      try {
        fitAddon.fit();
      } catch {
        // See the ResizeObserver comment above.
      }
      lastSizeRef.current = { cols: terminal.cols, rows: terminal.rows };
    }
    const size = lastSizeRef.current;
    if (size && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'resize', ...size }));
    }
  }

  function clearResumeSession() {
    attachingSessionIdRef.current = null;
    onClearResumeSession();
  }

  function disconnect() {
    terminalRef.current?.clearSelection();
    clearResumeSession();
    disconnectAttachment();
  }

  function sendTerminalInput(data: string) {
    send({ type: 'input', data });
  }

  function sendTerminalShortcut(key: Parameters<typeof consumeShortcut>[0]) {
    terminalRef.current?.clearSelection();
    sendTerminalInput(consumeShortcut(key));
  }

  function sendPrompt() {
    const value = promptValue.trimEnd();
    if (!value || connectionState !== 'attached') return;
    send({ type: 'prompt', text: value });
    setPromptValue('');
  }

  function stopSession() {
    terminalRef.current?.clearSelection();
    clearResumeSession();
    stopAttachment();
  }

  async function startNativeSession() {
    if (!canStartSessions) {
      return;
    }
    terminalRef.current?.clearSelection();
    // Starting a replacement session supersedes any earlier attachment intent
    // even while the new session is still being created.
    clearResumeSession();
    setStartBusy(true);
    setError(null);
    try {
      const body: CreateNativePtySessionRequest = {
        profile: startProfile,
        cols: lastSizeRef.current?.cols ?? 80,
        rows: lastSizeRef.current?.rows ?? 24
      };
      if (startProfile === 'generic-cli-pty' || startCommand.trim()) {
        body.command = startCommand.trim();
      }
      const session = await apiClient.post<Session>(`/api/conversations/${conversationId}/sessions/native-pty`, body);
      onSelectedSessionChange(session.id);
      terminalRef.current?.clear();
      onRefreshSessions();
      if (canAttachTerminal(session)) {
        await connect(session.id, session);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to start terminal session');
    } finally {
      setStartBusy(false);
    }
  }

  async function startSidecarShell() {
    if (!canStartSessions || !selectedSidecarParent) {
      return;
    }
    terminalRef.current?.clearSelection();
    // A sidecar shell is likewise a deliberate switch away from the prior
    // session, not a second resumable attachment.
    clearResumeSession();
    setStartBusy(true);
    setError(null);
    try {
      const body: CreateSidecarShellSessionRequest = {
        cols: lastSizeRef.current?.cols ?? 80,
        rows: lastSizeRef.current?.rows ?? 24
      };
      if (sidecarCommand.trim()) {
        body.command = sidecarCommand.trim();
      }
      const session = await apiClient.post<Session>(
        `/api/conversations/${conversationId}/sessions/${selectedSidecarParent.id}/sidecar-shell`,
        body
      );
      onSelectedSessionChange(session.id);
      terminalRef.current?.clear();
      onRefreshSessions();
      if (canAttachTerminal(session)) {
        await connect(session.id, session);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to start sidecar shell');
    } finally {
      setStartBusy(false);
    }
  }

  const startControls = canStartSessions ? (
    <>
      <form
        className="terminal-panel__start terminal-panel__start--session"
        onSubmit={(event) => {
          event.preventDefault();
          void startNativeSession();
        }}
      >
        <select
          value={startProfile}
          onChange={(event) => {
            const nextProfile = event.target.value as CreateNativePtySessionRequest['profile'];
            setStartProfile(nextProfile);
            setStartCommand((current) => {
              if (nextProfile === 'generic-cli-pty') {
                return current.trim() ? current : 'bash -l';
              }
              return current.trim() === 'bash -l' ? '' : current;
            });
          }}
          disabled={startBusy}
        >
          <option value="generic-cli-pty">Shell</option>
          <option value="codex-cli-pty">Codex CLI</option>
          <option value="claude-cli-pty">Claude CLI</option>
        </select>
        <input
          type="text"
          value={startCommand}
          placeholder={startProfile === 'generic-cli-pty' ? 'Command to run' : 'Optional command override'}
          onChange={(event) => setStartCommand(event.target.value)}
          disabled={startBusy}
        />
        <button type="submit" disabled={startBusy || (startProfile === 'generic-cli-pty' && !startCommand.trim())}>
          {startBusy ? 'Starting...' : 'Start session'}
        </button>
      </form>
      {structuredSessions.length > 0 && (
        <form
          className="terminal-panel__start terminal-panel__start--session"
          onSubmit={(event) => {
            event.preventDefault();
            void startSidecarShell();
          }}
        >
          <select
            value={selectedSidecarParent?.id ?? ''}
            onChange={(event) => setSidecarParentSessionId(event.target.value || null)}
            disabled={startBusy}
          >
            {structuredSessions.map((session) => (
              <option key={session.id} value={session.id}>
                Open shell for {session.profile ?? session.origin} · {session.status}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={sidecarCommand}
            placeholder="Sidecar command (default bash -l)"
            onChange={(event) => setSidecarCommand(event.target.value)}
            disabled={startBusy}
          />
          <button type="submit" disabled={startBusy || !selectedSidecarParent}>
            {startBusy ? 'Starting...' : 'Open sidecar shell'}
          </button>
        </form>
      )}
    </>
  ) : null;

  const toggleTerminalMaximize = useCallback(() => {
    setTerminalContextOpen(false);
    setTerminalSelectionMode(false);
    void toggleMaximize();
  }, [toggleMaximize]);

  const toggleTerminalSelectionMode = useCallback(() => {
    const terminal = terminalRef.current;
    terminal?.clearSelection();
    setTerminalSelectionMode((active) => {
      const next = !active;
      if (next) terminal?.blur();
      return next;
    });
  }, []);

  const copySelection = useCallback(async () => {
    const terminal = terminalRef.current;
    const selection = terminal?.getSelection() ?? '';
    if (!terminal || !selection) return;
    if (await copyTerminalText(selection)) {
      terminal.clearSelection();
      setTerminalContextOpen(false);
      setTerminalSelectionMode(false);
      return;
    }
    setGlobalError('Unable to copy the selected terminal text');
  }, [setGlobalError]);

  const pasteClipboard = useCallback(async () => {
    if (connectionState !== 'attached') return;
    const terminal = terminalRef.current;
    if (!terminal) return;
    const text = await readTerminalClipboardText();
    if (text === null) {
      setGlobalError('Unable to paste terminal clipboard. Allow clipboard access and try again.');
      return;
    }
    // xterm's public paste path preserves the terminal's normal newline and
    // bracketed-paste behavior rather than treating clipboard text as output.
    try {
      terminal.paste(text);
      terminal.clearSelection();
      setTerminalContextOpen(false);
      setTerminalSelectionMode(false);
    } catch {
      setGlobalError('Unable to paste terminal clipboard. Allow clipboard access and try again.');
    }
  }, [connectionState, setGlobalError]);

  return (
    <div
      ref={panelRef}
      className={`terminal-panel message-bubble message-bubble--system${isMaximized ? ' terminal-panel--maximized' : ''}`}
      style={terminalViewportStyle}
      data-terminal-keyboard={terminalKeyboardState}
      data-terminal-keyboard-guard={terminalKeyboardOverdrawGuard}
      data-terminal-viewport-frame={terminalViewportFrameLayout}
      data-terminal-touch-scroll="enabled"
      data-terminal-scroll-mode={scrollMode}
    >
      <div className="terminal-panel__viewport-frame">
        <TerminalFullscreenExitButton onExit={toggleTerminalMaximize} />
        <TerminalSelectionActions
          visible={isMaximized || terminalContextOpen}
          onCopy={() => void copySelection()}
          onPaste={() => void pasteClipboard()}
          copyDisabled={!hasTerminalSelection}
          pasteDisabled={connectionState !== 'attached'}
          selectionMode={terminalSelectionMode}
          onToggleSelectionMode={isMaximized ? toggleTerminalSelectionMode : undefined}
        />
        <div className="terminal-panel__header">
          <div>
            <div className="message-meta">
              <span>Terminal session</span>
              <span>{connectionState}</span>
            </div>
            {terminalSessions.length > 0 && (
              <div className="terminal-panel__session-line">
                <select
                  value={selectedId ?? ''}
                  onChange={(event) => {
                    disconnect();
                    onSelectedSessionChange(event.target.value || null);
                    setError(null);
                    setGlobalError(null);
                    terminalRef.current?.clear();
                  }}
                >
                  {terminalSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.profile ?? session.origin} · {session.status}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {showTerminalWorkspace && (
            <div className="row">
              <button
                type="button"
                onClick={() => void connect()}
                disabled={
                  !selectedId ||
                  !selectedSessionAttachable ||
                  connectionState === 'connecting'
                }
              >
                {connectionState === 'attached'
                  ? 'Reconnect'
                  : connectionState === 'reconnecting'
                    ? 'Retry now'
                    : 'Attach'}
              </button>
              <button type="button" className="ghost" onClick={disconnect} disabled={connectionState !== 'attached'}>
                Detach
              </button>
              {canStartSessions && <button type="button" className="secondary" onClick={stopSession} disabled={connectionState !== 'attached'}>
                Stop
              </button>}
            </div>
          )}
        </div>
        {error && <div className="message-error">{error}</div>}
        {showTerminalWorkspace ? (
          <>
            {startControls}
            <div className="terminal-panel__screen-wrap">
              <div className="terminal-panel__screen-actions">
                <button
                  type="button"
                  className="ghost terminal-panel__clipboard-action"
                  onClick={() => void copySelection()}
                  disabled={!hasTerminalSelection}
                  aria-label="Copy selected terminal text"
                  title="Copy selected terminal text"
                >
                  Copy
                </button>
                <button
                  type="button"
                  className="ghost terminal-panel__clipboard-action"
                  onClick={() => void pasteClipboard()}
                  disabled={connectionState !== 'attached'}
                  aria-label="Paste terminal clipboard"
                  title="Paste terminal clipboard"
                >
                  Paste
                </button>
                <TerminalScrollModeControl mode={scrollMode} onChange={setScrollMode} />
                <TerminalFontSizeControl />
                <button
                  type="button"
                  className="ghost terminal-panel__maximize terminal-panel__screen-action"
                  onClick={toggleTerminalMaximize}
                  aria-pressed={isMaximized}
                  aria-label={isMaximized ? 'Exit terminal full screen' : 'Enter terminal full screen'}
                  title={isMaximized ? 'Exit full screen' : 'Full screen'}
                >
                  ⛶
                </button>
              </div>
              <div
                ref={terminalHostRef}
                className="terminal-panel__screen"
                role="application"
                aria-label="Executor terminal"
              />
            </div>
            <TerminalSpecialKeyToolbar
              disabled={connectionState !== 'attached'}
              onFocus={focusTerminal}
              modifiers={modifiers}
              onToggleModifier={toggleModifier}
              onShortcut={sendTerminalShortcut}
              debugAction={terminalViewportDebugEnabled ? {
                accessibleLabel: 'Paste terminal diagnostics',
                label: 'Debug',
                onActivate: pasteTerminalViewportDebugReport,
                title: 'Paste viewport diagnostics without sending Enter'
              } : undefined}
            />
            {!selectedSession && (
              <div className="terminal-panel__placeholder">
            {canStartSessions
              ? 'Start a native CLI session to attach an executor terminal.'
              : 'No attachable terminal session is available.'}
              </div>
            )}
            {canStartSessions && selectedSession && canAcceptPrompt(selectedSession) && (
              <div className="terminal-panel__inputs">
                <form
                  className="terminal-panel__input-row"
                  onSubmit={(event) => {
                    event.preventDefault();
                    sendPrompt();
                  }}
                >
                  <input
                    type="text"
                    value={promptValue}
                    placeholder="Prompt composer input"
                    onChange={(event) => setPromptValue(event.target.value)}
                    disabled={connectionState !== 'attached'}
                  />
                  <button type="submit" disabled={connectionState !== 'attached' || !promptValue.trim()}>
                    Send prompt
                  </button>
                </form>
              </div>
            )}
          </>
        ) : (
          <details className="terminal-panel__tools">
            <summary>Terminal tools</summary>
            {startControls ?? <p className="hint">You can attach only to an existing terminal session.</p>}
          </details>
        )}
      </div>
    </div>
  );
}
